import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Hero video lightbox.
 *
 * WP opens the "What Makes Zager Special" video through jQuery Fancybox
 * (`functions.php:22,52`, triggered by a bare `data-fancybox` attribute).
 * jQuery is banned in this theme, so this is a small vanilla replacement: a
 * native `<dialog>` whose YouTube iframe is only created on click — nothing
 * loads on page view.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button that opens the dialog.
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} closeButton - The dialog's close control.
 * @property {HTMLDivElement} frame - Empty container the iframe is injected into.
 *
 * @extends {Component<Refs>}
 */
class ZagerHeroVideoComponent extends Component {
  requiredRefs = ['trigger', 'dialog', 'closeButton', 'frame'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('click', this.#onBackdropClick);
    this.refs.dialog.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog.removeEventListener('click', this.#onBackdropClick);
    this.refs.dialog.removeEventListener('close', this.#onClose);
    unlockScroll(this.refs.dialog);
  }

  /**
   * Opens the dialog, lazily inserting the YouTube iframe on first open.
   */
  open() {
    const { dialog, frame } = this.refs;
    const videoId = this.getAttribute('data-youtube-id');

    if (videoId && !frame.hasChildNodes()) {
      const iframe = document.createElement('iframe');

      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
      iframe.title = this.getAttribute('data-video-title') || 'Video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';

      frame.append(iframe);
    }

    lockScroll(dialog);
    dialog.showModal();
  }

  /**
   * Closes the dialog. The `close` event handles cleanup and returning
   * focus, so this covers the close button — Escape and `dialog.close()`
   * from elsewhere trigger the same event.
   */
  close() {
    this.refs.dialog.close();
  }

  /**
   * A click that lands on the dialog element itself (not one of its
   * children) is a click on the `::backdrop` — close in that case.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /**
   * Fires for every close path (Escape, the close button, or a backdrop
   * click calling `close()`): empties the iframe so playback stops and
   * nothing keeps loading in the background, then returns focus to the
   * trigger that opened the dialog.
   */
  #onClose = () => {
    this.refs.frame.replaceChildren();
    unlockScroll(this.refs.dialog);
    this.refs.trigger.focus();
  };
}

if (!customElements.get('zager-hero-video-component')) {
  customElements.define('zager-hero-video-component', ZagerHeroVideoComponent);
}
